import { CreditCardOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { appActions } from 'reduxStore/app/slice';
import { selectSites } from 'reduxStore/sites/selectors';
import { Button, Flex, Modal, Typography } from 'zui/Antd';

import { BatchIR, ProvisioningProgress } from './components';

const { Title, Text } = Typography;

const AddCardsBulkModal = ({ visible, setVisible }) => {
	const canAddCredential = useManagePermittedRoles('addCredential');
	const isGlobalAdmin = useManagePermittedRoles('manageSiteAssignment');
	const {
		token: {
			paddingSM,
			paddingMD,
			paddingXL,
			marginLG,
			colorBorderSecondary,
			fontSizeSM,
			colorPrimary,
			colorPrimaryBg,
			colorTextDescription,
			borderRadiusLG
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const bulkProgress = useSelector((state) => state.App.sync.bulkCredentialProgress);
	const { orgID } = useSelector((state) => state.Auth.selectedProfile);
	const sites = useSelector(selectSites);

	// -- Site selection
	const [selectedSiteIDs, setSelectedSiteIDs] = useState([]);

	useEffect(() => {
		if (visible && !isGlobalAdmin && sites.length > 0 && selectedSiteIDs.length === 0) {
			setSelectedSiteIDs([sites[0].siteID]);
		}
	}, [visible, isGlobalAdmin, sites, selectedSiteIDs.length]);

	// -- IR Batch state
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [startCardNumber, setStartCardNumber] = useState('');
	const [numberOfCards, setNumberOfCards] = useState(null);
	const [associateCSN, setAssociateCSN] = useState(false);
	const [csnMap, setCsnMap] = useState({});

	// -- Provisioning state
	const [provisioningProgress, setProvisioningProgress] = useState(0);
	const [provisioningLogs, setProvisioningLogs] = useState([]);
	const [provisioningSummary, setProvisioningSummary] = useState(null);
	const [failedCards, setFailedCards] = useState([]);
	const [provisioningStarted, setProvisioningStarted] = useState(false);

	// -- Permission guard
	useEffect(() => {
		if (visible && !canAddCredential) setVisible(false);
	}, [visible, canAddCredential, setVisible]);

	// -- Socket progress listener
	useEffect(() => {
		if (!provisioningStarted || !bulkProgress) return;

		if (bulkProgress.complete) {
			setProvisioningProgress(100);
			setProvisioningSummary({
				total: bulkProgress.total,
				succeeded: bulkProgress.succeeded,
				failed: bulkProgress.failed?.length || 0
			});
			setFailedCards(bulkProgress.failed || []);
			return;
		}

		const { processed, total, lastCard } = bulkProgress;
		if (!processed || !total) return;

		setProvisioningProgress(Math.round((processed / total) * 100));
		if (lastCard) {
			setProvisioningLogs((prev) => [
				{
					id: Date.now() + Math.random(),
					timestamp: new Date().toLocaleTimeString(),
					cardserialnumber: lastCard.uniqueIdentifier || '',
					cardNumber: lastCard.value || '',
					cardName: lastCard.name || '',
					success: lastCard.success,
					error: lastCard.error || null
				},
				...prev
			]);
		}
	}, [bulkProgress, provisioningStarted]);

	// -- Can provision?
	const canProvisionIR = selectedTemplate && startCardNumber?.length > 0 && numberOfCards > 0;

	// -- Dispatch optimized batch endpoint
	const handleProvision = () => {
		if (!canAddCredential || !canProvisionIR) return;

		setProvisioningProgress(0);
		setProvisioningLogs([]);
		setProvisioningSummary(null);
		setFailedCards([]);
		setProvisioningStarted(true);

		dispatch(appActions.clearBulkCredentialProgress());
		const csns = associateCSN ? Array.from({ length: numberOfCards }, (_, i) => csnMap[i] || null) : undefined;

		dispatch(
			credentialsActions.createCardsBatchIR({
				data: {
					orgID,
					externalCredentialTemplateID: selectedTemplate?.credentialTemplateID || null,
					startCardNumber,
					numberOfCards,
					siteIDs: selectedSiteIDs,
					...(csns ? { csns } : {})
				}
			})
		);
	};

	// -- Reset
	const resetAndClose = () => {
		setSelectedTemplate(null);
		setStartCardNumber('');
		setNumberOfCards(null);
		setAssociateCSN(false);
		setCsnMap({});
		setSelectedSiteIDs([]);
		setProvisioningProgress(0);
		setProvisioningLogs([]);
		setProvisioningSummary(null);
		setFailedCards([]);
		setProvisioningStarted(false);
		dispatch(appActions.clearBulkCredentialProgress());
		setVisible(false);
	};

	const handleClose = () => {
		if (provisioningStarted && !provisioningSummary) {
			Modal.confirm({
				title: 'Cancel provisioning?',
				content: 'Cards are still being provisioned. Are you sure you want to close?',
				okButtonProps: { style: { backgroundColor: colorPrimary, borderColor: colorPrimary } },
				onOk: () => resetAndClose()
			});
		} else {
			resetAndClose();
		}
	};

	// -- Stats for provisioning view
	const irStats = useMemo(
		() => ({
			ok: numberOfCards || 0,
			warn: numberOfCards || 0,
			error: 0,
			total: numberOfCards || 0
		}),
		[numberOfCards]
	);

	// -- Footer
	const renderFooter = () => {
		if (provisioningStarted && !provisioningSummary) {
			return (
				<Flex justify='space-between' align='center'>
					<Text type='secondary' style={{ fontSize: fontSizeSM }}>
						Provisioning via socket — do not close this window.
					</Text>
				</Flex>
			);
		}

		if (provisioningStarted && provisioningSummary) {
			return (
				<Flex justify='flex-end' gap={paddingSM}>
					<Button type='primary' onClick={resetAndClose}>
						Close
					</Button>
				</Flex>
			);
		}

		return (
			<Flex justify='space-between' align='center'>
				<Text type='secondary' style={{ fontSize: fontSizeSM }}>
					Inner Range batch cards
				</Text>
				<Flex gap={paddingSM}>
					<Button onClick={handleClose}>Cancel</Button>
					<Button type='primary' onClick={handleProvision} disabled={!canProvisionIR}>
						Provision {numberOfCards || 0} cards
					</Button>
				</Flex>
			</Flex>
		);
	};

	// -- Content
	const renderContent = () => {
		if (provisioningStarted) {
			return (
				<div style={{ padding: paddingXL }}>
					<ProvisioningProgress
						provisioningProgress={provisioningProgress}
						provisioningSummary={provisioningSummary}
						provisioningLogs={provisioningLogs}
						stats={irStats}
						failedCards={failedCards}
					/>
				</div>
			);
		}

		return (
			<div style={{ padding: paddingXL }}>
				<BatchIR
					selectedTemplate={selectedTemplate}
					setSelectedTemplate={setSelectedTemplate}
					startCardNumber={startCardNumber}
					setStartCardNumber={setStartCardNumber}
					numberOfCards={numberOfCards}
					setNumberOfCards={setNumberOfCards}
					associateCSN={associateCSN}
					setAssociateCSN={setAssociateCSN}
					csnMap={csnMap}
					setCsnMap={setCsnMap}
					selectedSiteIDs={selectedSiteIDs}
					setSelectedSiteIDs={setSelectedSiteIDs}
				/>
			</div>
		);
	};

	return (
		<Modal
			open={visible}
			width={associateCSN ? 1100 : 960}
			centered
			closeIcon={false}
			destroyOnClose
			keyboard={false}
			maskClosable={false}
			styles={{
				content: { padding: 0 },
				body: { padding: 0, maxHeight: '70vh', overflowY: 'auto' },
				footer: { padding: `${paddingSM}px ${paddingXL}px`, borderTop: `1px solid ${colorBorderSecondary}` }
			}}
			footer={renderFooter()}
		>
			<Flex align='flex-start' gap={paddingMD} style={{ padding: `${paddingXL}px ${paddingXL}px ${marginLG}px` }}>
				<Flex
					align='center'
					justify='center'
					style={{
						width: 44,
						height: 44,
						background: colorPrimaryBg,
						borderRadius: borderRadiusLG,
						flexShrink: 0
					}}
				>
					<CreditCardOutlined style={{ fontSize: 20, color: colorPrimary }} />
				</Flex>
				<Flex vertical gap={2}>
					<Title level={4} style={{ margin: 0 }}>
						Add Batch Cards
					</Title>
					<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
						Create multiple access cards at once for your organization.
					</Text>
				</Flex>
			</Flex>

			{renderContent()}
		</Modal>
	);
};

export default AddCardsBulkModal;
